import { FormEvent, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError, Organization, User, UserPage, api } from "../lib/api";
import { useAuth } from "../lib/auth";

type Role = User["role"];

const ROLE_OPTIONS: { value: Role; label: string; help: string }[] = [
  { value: "owner", label: "Owner", help: "Full powers; manage users, models, orgs, and see every case" },
  { value: "admin", label: "Admin", help: "Manage users, view activity, see every case in their org" },
  { value: "lawyer", label: "Lawyer", help: "Standard team member; cases must be assigned" },
  { value: "assistant", label: "Assistant", help: "Same as Lawyer; lower-privilege convention" },
  { value: "viewer", label: "Viewer", help: "Read-only on assigned cases; cannot create cases" },
];

interface CreateForm {
  email: string;
  display_name: string;
  password: string;
  password_confirm: string;
  role: Role;
  organization_id: number | null;
}

const EMPTY_CREATE: CreateForm = {
  email: "",
  display_name: "",
  password: "",
  password_confirm: "",
  role: "lawyer",
  organization_id: null,
};

interface EditForm {
  display_name: string;
  role: Role;
  is_active: boolean;
  password: string;
  organization_id: number;
}

export default function AdminUsers() {
  const me = useAuth((s) => s.user);
  const impersonate = useAuth((s) => s.impersonate);
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [totalUsers, setTotalUsers] = useState(0);

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState<CreateForm>(EMPTY_CREATE);
  const [creating, setCreating] = useState(false);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<EditForm | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [impersonatingId, setImpersonatingId] = useState<number | null>(null);

  const isAdmin = me?.role === "owner" || me?.role === "admin";
  const isOwner = me?.role === "owner";

  const [orgs, setOrgs] = useState<Organization[]>([]);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const pageSize = 20;

  useEffect(() => {
    if (!isAdmin) return;
    void load(page, search);
  }, [isAdmin, isOwner, page, search]);

  // Owners may place new users in any organization; load the list once.
  useEffect(() => {
    if (!isOwner) return;
    void (async () => {
      try {
        const list = await api.get<Organization[]>("/organizations");
        setOrgs(list);
      } catch {
        // Non-fatal — the create form will just hide the org picker.
      }
    })();
  }, [isOwner]);

  async function load(pageToLoad = page, searchTerm = search) {
    setBusy(true);
    setErr(null);
    try {
      const params = new URLSearchParams({
        limit: String(pageSize),
        offset: String(pageToLoad * pageSize),
      });
      if (isOwner) params.set("scope", "all");
      const q = searchTerm.trim();
      if (q) params.set("q", q);
      const data = await api.get<UserPage>(`/users/page?${params.toString()}`);
      setUsers(data.items);
      setTotalUsers(data.total);
      if (data.total > 0 && data.items.length === 0 && pageToLoad > 0) {
        setPage(Math.max(0, Math.ceil(data.total / pageSize) - 1));
      }
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.message : "Failed to load users");
    } finally {
      setBusy(false);
    }
  }

  function openEdit(u: User) {
    setEditingId(u.id);
    setEditForm({
      display_name: u.display_name,
      role: u.role,
      is_active: u.is_active,
      password: "",
      organization_id: u.organization_id,
    });
  }

  // Snapshot of the user being edited (for the modal header / move-warning).
  const editingUser = useMemo(
    () => (editingId == null ? null : users.find((u) => u.id === editingId) ?? null),
    [editingId, users],
  );

  function closeEdit() {
    setEditingId(null);
    setEditForm(null);
  }

  async function submitCreate(ev: FormEvent) {
    ev.preventDefault();
    setErr(null);
    if (createForm.password.length < 8) {
      setErr("Password must be at least 8 characters.");
      return;
    }
    if (createForm.password !== createForm.password_confirm) {
      setErr("Passwords don't match.");
      return;
    }
    setCreating(true);
    try {
      // Only send organization_id when the owner explicitly picked a *different*
      // org than their own — keeps the request shape unchanged for the common
      // case and for admins.
      const targetOrgId =
        createForm.organization_id ?? me?.organization_id ?? null;
      const isCrossOrg =
        targetOrgId != null && targetOrgId !== me?.organization_id;
      const created = await api.post<User>("/users", {
        email: createForm.email.trim().toLowerCase(),
        password: createForm.password,
        display_name: createForm.display_name.trim(),
        role: createForm.role,
        ...(isCrossOrg ? { organization_id: targetOrgId } : {}),
      });
      setPage(0);
      await load(0, search);
      setCreateForm(EMPTY_CREATE);
      setShowCreate(false);
      const targetOrg = orgs.find((o) => o.id === created.organization_id);
      const orgSuffix =
        isCrossOrg && targetOrg ? ` in ${targetOrg.name}` : "";
      flashFor(`Created ${created.email}${orgSuffix}.`);
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.message : "Failed to create user");
    } finally {
      setCreating(false);
    }
  }

  async function submitEdit(ev: FormEvent) {
    ev.preventDefault();
    if (editingId == null || !editForm || !editingUser) return;
    setErr(null);

    const body: Record<string, unknown> = {
      display_name: editForm.display_name.trim(),
      role: editForm.role,
      is_active: editForm.is_active,
    };
    if (editForm.password.trim()) {
      if (editForm.password.length < 8) {
        setErr("New password must be at least 8 characters.");
        return;
      }
      body.password = editForm.password;
    }
    const orgChanged =
      isOwner && editForm.organization_id !== editingUser.organization_id;
    if (orgChanged) {
      body.organization_id = editForm.organization_id;
    }

    setSavingEdit(true);
    try {
      const updated = await api.patch<User>(`/users/${editingId}`, body);
      await load(page, search);
      const targetOrg = orgs.find((o) => o.id === updated.organization_id);
      const moveSuffix =
        orgChanged && targetOrg ? ` (moved to ${targetOrg.name})` : "";
      const pwSuffix = editForm.password.trim() ? " (password reset)" : "";
      flashFor(`Updated ${updated.email}${pwSuffix}${moveSuffix}.`);
      closeEdit();
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.message : "Failed to update user");
    } finally {
      setSavingEdit(false);
    }
  }

  function flashFor(msg: string) {
    setFlash(msg);
    setTimeout(() => setFlash((m) => (m === msg ? null : m)), 3500);
  }

  async function loginAs(u: User) {
    if (!isOwner || u.id === me?.id) return;
    setErr(null);
    setImpersonatingId(u.id);
    try {
      await impersonate(u.id);
      navigate("/", { replace: true });
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.message : "Failed to login as user");
    } finally {
      setImpersonatingId(null);
    }
  }

  const sortedUsers = useMemo(() => {
    const sorted = [...users].sort((a, b) => {
      const byOrg = orgLabel(a, orgs).localeCompare(orgLabel(b, orgs));
      return byOrg || a.display_name.localeCompare(b.display_name);
    });
    return sorted;
  }, [users, orgs]);

  const totalPages = Math.ceil(totalUsers / pageSize);
  const showOrganizationColumn = isOwner;
  const tableColumnCount = showOrganizationColumn ? 7 : 6;

  if (!isAdmin) {
    return (
      <div className="admin">
        <header className="admin__head">
          <div>
            <div className="page-eyebrow">Users</div>
            <h1>Users</h1>
          </div>
        </header>
        <div className="alert alert--error" role="alert">
          User administration is restricted to organization owners and admins.
        </div>
      </div>
    );
  }

  return (
    <div className="admin admin--page-scroll">
      <header className="admin__head">
        <div>
          <div className="page-eyebrow">Site settings</div>
          <h1>Users</h1>
          <p className="admin__lead">
            Manage {isOwner ? "everyone in the system" : "everyone in your organization"}.
            Invite teammates, change roles, deactivate accounts, or reset passwords.
          </p>
        </div>
        <div className="admin__head-actions">
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => {
              setCreateForm(EMPTY_CREATE);
              setShowCreate(true);
              setErr(null);
            }}
          >
            + New user
          </button>
        </div>
      </header>

      {err && <div className="alert alert--error">{err}</div>}
      {flash && <div className="alert alert--ok">{flash}</div>}

      {showCreate && (
        <div
          className="modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="create-user-title"
          onClick={(e) => {
            if (e.target === e.currentTarget && !creating) {
              setShowCreate(false);
              setErr(null);
            }
          }}
        >
          <form
            className="modal__card modal__card--lg modal__card--split"
            onSubmit={submitCreate}
            onClick={(e) => e.stopPropagation()}
          >
            <header className="modal__header">
              <div className="modal__header-main">
                <h2 id="create-user-title" className="modal__header-title">
                  Invite a new user
                </h2>
                <p className="muted" style={{ margin: 0, fontSize: 13 }}>
                  They can sign in immediately with the password you set here.
                  Share it through a secure channel — they can rotate it from{" "}
                  <em>Update profile</em> after signing in.
                </p>
              </div>
              <button
                type="button"
                className="modal__close"
                onClick={() => {
                  if (!creating) {
                    setShowCreate(false);
                    setErr(null);
                  }
                }}
                aria-label="Close"
                title="Close"
              >
                ×
              </button>
            </header>

            <div className="modal__body">
              {err && <div className="alert alert--error">{err}</div>}

              <div className="admin-card__grid">
                <div className="admin-card__field admin-card__field--wide">
                  <label htmlFor="cu-name">Full name</label>
                  <input
                    id="cu-name"
                    type="text"
                    required
                    autoComplete="off"
                    value={createForm.display_name}
                    onChange={(e) => setCreateForm({ ...createForm, display_name: e.target.value })}
                  />
                </div>
                <div className="admin-card__field admin-card__field--wide">
                  <label htmlFor="cu-email">Email</label>
                  <input
                    id="cu-email"
                    type="email"
                    required
                    autoComplete="off"
                    value={createForm.email}
                    onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  />
                </div>
                <div className="admin-card__field admin-card__field--wide">
                  <label htmlFor="cu-role">Role</label>
                  <select
                    id="cu-role"
                    value={createForm.role}
                    onChange={(e) => setCreateForm({ ...createForm, role: e.target.value as Role })}
                  >
                    {ROLE_OPTIONS.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                  <div className="admin-card__hint muted">
                    {ROLE_OPTIONS.find((r) => r.value === createForm.role)?.help}
                  </div>
                </div>
                <div className="admin-card__field">
                  <label htmlFor="cu-pw">Initial password</label>
                  <input
                    id="cu-pw"
                    type="text"
                    required
                    autoComplete="new-password"
                    minLength={8}
                    value={createForm.password}
                    onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                  />
                  <div className="admin-card__hint muted">Min 8 characters.</div>
                </div>
                <div className="admin-card__field">
                  <label htmlFor="cu-pw2">Confirm password</label>
                  <input
                    id="cu-pw2"
                    type="text"
                    required
                    autoComplete="new-password"
                    minLength={8}
                    value={createForm.password_confirm}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, password_confirm: e.target.value })
                    }
                  />
                  {createForm.password_confirm.length > 0 &&
                    createForm.password !== createForm.password_confirm && (
                      <div className="admin-card__hint" style={{ color: "var(--danger, #d04545)" }}>
                        Passwords don't match.
                      </div>
                    )}
                </div>
                {isOwner && orgs.length > 0 && (
                  <div className="admin-card__field admin-card__field--wide">
                    <label htmlFor="cu-org">Organization</label>
                    <select
                      id="cu-org"
                      value={createForm.organization_id ?? me?.organization_id ?? ""}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          organization_id: e.target.value
                            ? Number(e.target.value)
                            : null,
                        })
                      }
                    >
                      {[...orgs]
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((o) => (
                          <option key={o.id} value={o.id}>
                            {o.name}
                            {o.id === me?.organization_id ? " (yours)" : ""}
                          </option>
                        ))}
                    </select>
                    <div className="admin-card__hint muted">
                      Defaults to your organization. Owners can add users to any tenant.
                    </div>
                  </div>
                )}
              </div>
            </div>

            <footer className="modal__footer">
              <span className="muted modal__footer-hint">
                The user can sign in right away.
              </span>
              <div className="modal__footer-actions">
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    setShowCreate(false);
                    setErr(null);
                  }}
                  disabled={creating}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn--primary" disabled={creating}>
                  {creating ? "Creating…" : "Create user"}
                </button>
              </div>
            </footer>
          </form>
        </div>
      )}

      <div className="admin-users-filter">
        <input
          type="search"
          className="admin-users-filter__input"
          placeholder={`Search by name, email${showOrganizationColumn ? ", organization" : ""}, or role…`}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
        />
        <span className="admin-users-filter__count muted">
          {totalUsers} user{totalUsers !== 1 ? "s" : ""}
          {search.trim() && ` matching "${search.trim()}"`}
        </span>
      </div>

      <div className="admin__scroll">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              {showOrganizationColumn && <th>Organization</th>}
              <th>Role</th>
              <th>Status</th>
              <th>2FA</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {busy && (
              <tr><td colSpan={tableColumnCount} className="muted">Loading…</td></tr>
            )}
            {!busy && totalUsers === 0 && (
              <tr><td colSpan={tableColumnCount} className="muted">{search.trim() ? "No users match your search." : "No users yet."}</td></tr>
            )}
            {sortedUsers.map((u) => {
              const isMe = me?.id === u.id;
              const canLoginAs = isOwner && !isMe && u.is_active;
              return (
                <tr key={u.id} className={u.is_active ? "" : "admin-table__row--inactive"}>
                  <td>
                    <div className="admin-table__name">
                      {u.display_name}
                      {isMe && <span className="admin-table__chip admin-table__chip--me">you</span>}
                    </div>
                  </td>
                  <td className="admin-table__mono">{u.email}</td>
                  {showOrganizationColumn && <td>{orgLabel(u, orgs)}</td>}
                  <td>
                    <span className={`admin-table__role admin-table__role--${u.role}`}>
                      {u.role}
                    </span>
                  </td>
                  <td>
                    {u.is_active ? (
                      <span className="admin-table__chip admin-table__chip--ok">active</span>
                    ) : (
                      <span className="admin-table__chip admin-table__chip--warn">inactive</span>
                    )}
                  </td>
                  <td>
                    {u.totp_enabled ? (
                      <span className="admin-table__chip admin-table__chip--ok">on</span>
                    ) : (
                      <span className="admin-table__chip">off</span>
                    )}
                  </td>
                  <td className="admin-table__actions">
                    {canLoginAs && (
                      <button
                        type="button"
                        className="btn btn--sm"
                        onClick={() => void loginAs(u)}
                        disabled={impersonatingId === u.id}
                      >
                        {impersonatingId === u.id ? "Switching…" : "Login as…"}
                      </button>
                    )}
                    <button type="button" className="btn" onClick={() => openEdit(u)}>
                      Edit
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="te-pagination">
          <button
            type="button"
            className="btn btn--sm"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            ← Prev
          </button>
          <span className="muted">
            Page {page + 1} of {totalPages} ({totalUsers} users)
          </span>
          <button
            type="button"
            className="btn btn--sm"
            disabled={page + 1 >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next →
          </button>
        </div>
      )}

      {editingId != null && editForm && (
        <div
          className="modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-user-title"
          onClick={(e) => {
            if (e.target === e.currentTarget && !savingEdit) closeEdit();
          }}
        >
          <form
            className="modal__card modal__card--lg modal__card--split"
            onSubmit={submitEdit}
            onClick={(e) => e.stopPropagation()}
          >
            <header className="modal__header">
              <div className="modal__header-main">
                <h2 id="edit-user-title" className="modal__header-title">
                  Edit user
                </h2>
                {editingUser && (
                  <span className="muted" style={{ fontSize: 13 }}>
                    {editingUser.display_name} · {editingUser.email}
                  </span>
                )}
              </div>
              <button
                type="button"
                className="modal__close"
                onClick={closeEdit}
                aria-label="Close"
                title="Close"
              >
                ×
              </button>
            </header>

            <div className="modal__body">
              {err && <div className="alert alert--error">{err}</div>}

              <div className="admin-card__grid">
                <div className="admin-card__field admin-card__field--wide">
                  <label htmlFor="eu-name">Full name</label>
                  <input
                    id="eu-name"
                    type="text"
                    required
                    value={editForm.display_name}
                    onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })}
                  />
                </div>
                <div className="admin-card__field">
                  <label htmlFor="eu-role">Role</label>
                  <select
                    id="eu-role"
                    value={editForm.role}
                    onChange={(e) => setEditForm({ ...editForm, role: e.target.value as Role })}
                    disabled={editingId === me?.id}
                    title={editingId === me?.id ? "You can't change your own role." : undefined}
                  >
                    {ROLE_OPTIONS.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
                <div className="admin-card__field">
                  <label>
                    <input
                      type="checkbox"
                      checked={editForm.is_active}
                      onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                      disabled={editingId === me?.id}
                    />
                    &nbsp;Account active
                  </label>
                  {editingId === me?.id && (
                    <div className="admin-card__hint muted">You can't deactivate yourself.</div>
                  )}
                </div>
                <div className="admin-card__field admin-card__field--wide">
                  <label htmlFor="eu-pw">Reset password (optional)</label>
                  <input
                    id="eu-pw"
                    type="text"
                    autoComplete="new-password"
                    placeholder="Leave blank to keep the current password"
                    value={editForm.password}
                    onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                  />
                  <div className="admin-card__hint muted">
                    Setting a value here forcibly resets the user's password. They can change it
                    themselves afterward from <em>Update profile</em>.
                  </div>
                </div>
                {isOwner && orgs.length > 0 && (
                  <div className="admin-card__field admin-card__field--wide">
                    <label htmlFor="eu-org">Organization</label>
                    <select
                      id="eu-org"
                      value={editForm.organization_id}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          organization_id: Number(e.target.value),
                        })
                      }
                      disabled={editingId === me?.id}
                      title={
                        editingId === me?.id
                          ? "You can't move yourself to another organization."
                          : undefined
                      }
                    >
                      {[...orgs]
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((o) => (
                          <option key={o.id} value={o.id}>
                            {o.name}
                            {o.id === me?.organization_id ? " (yours)" : ""}
                          </option>
                        ))}
                    </select>
                    {editingUser &&
                      editForm.organization_id !== editingUser.organization_id && (
                        <div
                          className="admin-card__hint"
                          style={{ color: "var(--danger, #d04545)" }}
                        >
                          Saving will move this user to another organization.
                        </div>
                      )}
                    {editingId === me?.id && (
                      <div className="admin-card__hint muted">
                        You can't move yourself to another organization.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <footer className="modal__footer">
              <span className="muted modal__footer-hint" />
              <div className="modal__footer-actions">
                <button type="button" className="btn" onClick={closeEdit} disabled={savingEdit}>
                  Cancel
                </button>
                <button type="submit" className="btn btn--primary" disabled={savingEdit}>
                  {savingEdit ? "Saving…" : "Save changes"}
                </button>
              </div>
            </footer>
          </form>
        </div>
      )}
    </div>
  );
}

function orgLabel(u: User, orgs: Organization[]): string {
  return (
    u.organization_name ??
    orgs.find((o) => o.id === u.organization_id)?.name ??
    `Org #${u.organization_id}`
  );
}
